import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useScrollSnap hook
 *
 * Provides logic for a scroll-snap carousel with manual arrow controls.
 * It handles tracking whether the container can scroll further in either direction
 * and provides methods to scroll to the next/previous slide based on [data-slide-index].
 */
export const useScrollSnap = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  const updateScrollState = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    // Use a small buffer (2px) for sub-pixel rounding issues
    setCanScrollPrev(container.scrollLeft > 2);
    setCanScrollNext(container.scrollLeft + container.clientWidth < container.scrollWidth - 2);
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return undefined;

    container.addEventListener('scroll', updateScrollState, { passive: true });
    updateScrollState();

    // Update on resize as well
    window.addEventListener('resize', updateScrollState);

    return () => {
      container.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState]);

  const scrollToSlide = useCallback((direction: 'prev' | 'next') => {
    const container = scrollRef.current;
    if (!container) return;

    const slides = container.querySelectorAll<HTMLElement>('[data-slide-index]');
    if (!slides.length) return;

    const containerRect = container.getBoundingClientRect();
    let closestIndex = 0;
    let closestDistance = Infinity;

    // Find the slide whose left edge is closest to the container's left edge
    slides.forEach((slide, i) => {
      const distance = Math.abs(slide.getBoundingClientRect().left - containerRect.left);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    });

    const targetIndex =
      direction === 'next' ? Math.min(closestIndex + 1, slides.length - 1) : Math.max(closestIndex - 1, 0);

    const targetSlide = slides[targetIndex];
    if (targetSlide) {
      const slideRect = targetSlide.getBoundingClientRect();
      container.scrollTo({
        left: container.scrollLeft + (slideRect.left - containerRect.left),
        behavior: 'smooth',
      });
    }
  }, []);

  return {
    scrollRef,
    canScrollPrev,
    canScrollNext,
    scrollToPrev: () => scrollToSlide('prev'),
    scrollToNext: () => scrollToSlide('next'),
    updateScrollState,
  };
};
